import React, { useEffect, useState } from 'react';
import { ChallengeMe, ChallengeSummary, challengeToday } from '@radevents/shared';
import { useSignedInUser } from '../contexts/signedInUser';
import { useBannerClick, useBannerImpression } from './adPreview';
import type { PromoControls } from './adBanners';
import { ChallengeTeaser } from './winterchallenge/ChallengeTeaser';

/**
 * Winterchallenge im Werbeplatz über der Eventliste (`docs/bereiche/winterchallenge.md`). Eine
 * Haus-Anzeige wie „Für Veranstalter“: die Redaktion schaltet sie unter `/admin/werbeplatz`,
 * Einblendung und Klick zählen als `AD_BANNER`-Slot `winterchallenge`.
 *
 * Der Stand kommt einmal je Seitenaufruf vom Server; bis er da ist, zeigt die Karte nur Titel und
 * Knopf, damit der Platz nicht springt. Nach der Saison verschwindet sie nicht von selbst, sondern
 * wird abgeschaltet — der Endstand ist auch eine Nachricht.
 */
const SLOT = 'winterchallenge';

/** `undefined` = noch nicht gefragt; `null` = gefragt, aber keine Saison bzw. nicht erreichbar. */
let cached: ChallengeSummary | null | undefined;

/**
 * Der Stand der Saison, einmal je Seitenaufruf. `loaded` sagt, ob die Antwort da ist: Kopf und
 * Tab-Leiste zeichnen ihre saisonalen Einträge erst dann, damit nichts springt, und ohne Saison
 * (404, nicht erreichbar) stehen sie gar nicht.
 */
export function useChallengeSummaryState(): { summary: ChallengeSummary | null; loaded: boolean } {
  const [state, setState] = useState<{ summary: ChallengeSummary | null; loaded: boolean }>(
    cached === undefined ? { summary: null, loaded: false } : { summary: cached, loaded: true },
  );
  useEffect(() => {
    if (cached !== undefined) return;
    let alive = true;
    // Erst bei Bedarf geladen: die Anzeigenliste (`adBanners`) wird überall importiert, auch in
    // Tests ohne API-Schicht; ein statischer Import zöge Axios in jede davon.
    import('../services/challengeService')
      .then(m => m.default.summary())
      .then(s => { cached = s; if (alive) setState({ summary: s, loaded: true }); })
      .catch(() => { cached = null; if (alive) setState({ summary: null, loaded: true }); });
    return () => { alive = false; };
  }, []);
  return state;
}

export function useChallengeSummary(): ChallengeSummary | null {
  return useChallengeSummaryState().summary;
}

/** Die eigene Teilnahme, je Konto einmal geladen; `null` = nicht dabei oder nicht angemeldet. */
let cachedMe: { userId: number; me: ChallengeMe | null } | null = null;

/** Nach Mitmachen oder Eintragen: der Hinweis lädt die eigenen Kilometer beim nächsten Mal neu. */
export function forgetChallengeMe() {
  cachedMe = null;
}

function useChallengeMe(userId: number | null): [ChallengeMe | null, (next: ChallengeMe) => void] {
  const hit = userId != null && cachedMe?.userId === userId ? cachedMe.me : null;
  const [me, setMe] = useState<ChallengeMe | null>(hit);
  useEffect(() => {
    if (userId == null) { setMe(null); return; }
    if (cachedMe?.userId === userId) { setMe(cachedMe.me); return; }
    let alive = true;
    import('../services/challengeService')
      .then(m => m.default.me())
      .then(result => { cachedMe = { userId, me: result }; if (alive) setMe(result); })
      .catch(() => {});
    return () => { alive = false; };
  }, [userId]);
  const update = (next: ChallengeMe) => {
    if (userId != null) cachedMe = { userId, me: next };
    setMe(next);
  };
  return [me, update];
}

export default function WinterchallengePromoBanner({ controls, summary: given, me: givenMe }: {
  controls?: PromoControls;
  summary?: ChallengeSummary | null;
  /** Nur Design-Lab: Teilnahme vorgeben statt laden. */
  me?: ChallengeMe | null;
}) {
  useBannerImpression(SLOT);
  const trackClick = useBannerClick(SLOT);
  const loaded = useChallengeSummary();
  const summary = given ?? loaded;
  // Wer schon mitmacht, sieht seine Kilometer und „Kilometer eintragen“ statt „Mitmachen“.
  const user = useSignedInUser();
  const [loadedMe, setMe] = useChallengeMe(givenMe === undefined && user ? user.id : null);
  const me = givenMe === undefined ? loadedMe : givenMe;
  const today = challengeToday();
  // Das Fenster hängt nicht in der Anzeige, sondern in `QuickRideHost` auf der Startseite. Erst
  // beim Klick geladen: das Formular zieht über `ChallengeParts` die API-Schicht mit.
  const openRide = () => {
    if (!me || !summary) return;
    import('./winterchallenge/QuickRideDialog').then(m => m.openQuickRide({ me, summary, today, onSaved: setMe })).catch(() => {});
  };
  return (
    <>
      <div className="hide-mobile">
        <ChallengeTeaser summary={summary} today={today} me={me} variant="desktop"
          onCta={() => trackClick('landing-desktop')} onAddRide={openRide} controls={controls?.('desktop')} />
      </div>
      <div className="hide-desktop">
        <ChallengeTeaser summary={summary} today={today} me={me} variant="mobile"
          onCta={() => trackClick('landing-mobile')} onAddRide={openRide} controls={controls?.('mobile')} />
      </div>
    </>
  );
}
